<template>
    <div class="modal fade" tabindex="-1" aria-labelledby="taxModalLabel" :id="id">
        <div class="modal-dialog modal-dialog-centered modal-xl">
            <div class="modal-content border-0 shadow-lg">
                <!-- ヘッダー -->
                <div class="modal-header border-0 bg-gradient-primary">
                    <h5 class="modal-title fw-semibold d-flex align-items-center gap-2">
                        <i class="fa-solid fa-file-pen"></i>
                        領収書一括編集
                    </h5>
                    <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>

                <!-- ボディ -->
                <div class="modal-body p-4" v-if="receipt_images.length > 0">
                    <div class="row g-4">
                        <!-- 左カラム: 画像プレビュー -->
                        <div class="col-lg-5">
                            <div class="image-preview-card">
                                <div v-for="receipt_image in receipt_images" :key="receipt_image.id" class="mb-3">
                                    <div class="image-wrapper">
                                        <preview-file :receipt_image="receipt_image" :receipt="receipt" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 右カラム: フォーム -->
                        <div class="col-lg-7">
                            <div class="form-section mb-3" v-if="isOverwrite">
                                記入した部分だけ一括上書きします。
                            </div>
                            <!-- 基本情報セクション -->
                            <div class="form-section mb-3">
                                <h6 class="section-title">
                                    <i class="fa-solid fa-circle-info me-2"></i>基本情報
                                </h6>

                                <div class="row g-3">
                                    <div class="col-md-6">
                                        <label class="form-label">購入日付</label>
                                        <single-picker
                                            v-model="receipt.purchase_date"
                                            placeholder="日付を選択"
                                            :max-date="new Date()"
                                            :week-start="0"
                                            :class="{'is-invalid': errors.purchase_date}"
                                            data-error-key="purchase_date"
                                        />
                                        <div v-if="errors.purchase_date" class="invalid-feedback d-block">
                                            {{ errors.purchase_date }}
                                        </div>
                                    </div>

                                    <div class="col-md-6">
                                        <label class="form-label">取引先</label>
                                        <dropdown-selector
                                            v-model="receipt.client"
                                            placeholder="取引先を選択"
                                            label="label"
                                            :class="{'is-invalid': errors.client_id}"
                                            :options="clients"
                                        ></dropdown-selector>
                                        <div v-if="errors.client_id" class="invalid-feedback d-block">
                                            {{ errors.client_id }}
                                        </div>
                                    </div>

                                    <div class="col-12">
                                        <label class="form-label">タグ</label>
                                        <dropdown-selector
                                            v-model="receipt.tag"
                                            placeholder="タグを選択（任意）"
                                            label="name"
                                            :class="{'is-invalid': errors.tag_id}"
                                            :options="tags"
                                        ></dropdown-selector>
                                    </div>
                                </div>
                            </div>

                            <!-- 明細セクション -->
                            <div class="form-section mb-3">
                                <div class="mb-3">
                                    <div class="d-flex justify-content-between">
                                        <h6 class="section-title flex-grow-1 mb-0 me-3">
                                            <i class="fa-solid fa-list me-2"></i>明細
                                        </h6>
                                        <div>
                                            <button class="btn btn-sm btn-outline-primary" @click="addDetail" tabindex="-1">
                                                <i class="fa-solid fa-plus me-1"></i>
                                                明細追加
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div class="details-container">
                                    <div v-for="(d, i) in receipt.receipt_details" :key="i" class="detail-card">
                                        <div class="d-flex justify-content-between">
                                            <div class="detail-header flex-grow-1 me-3">
                                                <span class="detail-number">明細</span>
                                            </div>
                                            <div>
                                                <button class="btn btn-sm btn-outline-danger" @click="removeDetail(i)" tabindex="-1" :disabled="receipt.receipt_details.length == 1 && i == 0">
                                                    <i class="fa-solid fa-xmark"></i>
                                                    抹消
                                                </button>
                                            </div>
                                        </div>

                                        <div class="row g-3">
                                            <div class="col-md-6">
                                                <label class="form-label">勘定科目</label>
                                                <dropdown-selector
                                                    v-model="d.account_title"
                                                    placeholder="勘定科目を選択"
                                                    label="label"
                                                    :large="true"
                                                    :class="{'is-invalid': errors[`account_title_id_${i}`]}"
                                                    :options="account_titles"
                                                ></dropdown-selector>
                                                <div v-if="errors[`account_title_id_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`account_title_id_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-md-6">
                                                <label class="form-label">金額</label>
                                                <div class="input-group input-group-lg">
                                                    <span class="input-group-text">¥</span>
                                                    <input
                                                        type="number"
                                                        v-model="d.amount"
                                                        class="form-control form-control-lg"
                                                        :class="{'is-invalid': errors[`amount_${i}`]}"
                                                        @change="calcTax(d)"
                                                        placeholder="0"
                                                    />
                                                </div>
                                                <div v-if="errors[`amount_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`amount_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-md-4">
                                                <label class="form-label">消費税率</label>
                                                <select
                                                    v-model="d.tax_rate"
                                                    class="form-select"
                                                    @change="calcTax(d)"
                                                    :class="{'is-invalid': errors[`tax_rate_${i}`]}"
                                                >
                                                    <option :value="10">10%</option>
                                                    <option :value="8">8%</option>
                                                    <option :value="0">0%</option>
                                                </select>
                                                <div v-if="errors[`tax_rate_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`tax_rate_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-md-8">
                                                <label class="form-label">消費税</label>
                                                <div class="input-group">
                                                    <span class="input-group-text">¥</span>
                                                    <input
                                                        type="number"
                                                        v-model="d.tax"
                                                        class="form-control"
                                                        :class="{'is-invalid': errors[`tax_${i}`]}"
                                                        placeholder="0"
                                                    />
                                                    <button
                                                        type="button"
                                                        class="btn btn-outline-secondary fw-bold"
                                                        tabindex="-1"
                                                        @click="setTaxInclusive(d)">
                                                        税込み
                                                    </button>
                                                    <button
                                                        type="button"
                                                        class="btn btn-outline-secondary"
                                                        tabindex="-1"
                                                        @click="setTaxExclusive(d)">
                                                        税抜き
                                                    </button>
                                                </div>
                                                <div v-if="errors[`tax_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`tax_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-12">
                                                <label class="form-label">税区分</label>
                                                <dropdown-selector
                                                    v-model="d.tax_category"
                                                    placeholder="税区分を選択"
                                                    label="label"
                                                    :class="{'is-invalid': errors[`tax_category_id_${i}`]}"
                                                    :options="tax_categories"
                                                ></dropdown-selector>
                                                <div v-if="errors[`tax_category_id_${i}`]" class="invalid-feedback d-block">
                                                    {{ errors[`tax_category_id_${i}`] }}
                                                </div>
                                            </div>

                                            <div class="col-md-6">
                                                <label class="form-label text-muted">摘要（任意）</label>
                                                <input
                                                    type="text"
                                                    v-model="d.description"
                                                    class="form-control"
                                                    placeholder="摘要を入力"
                                                />
                                            </div>

                                            <div class="col-md-6">
                                                <label class="form-label text-muted">TKC伝票番号（任意）</label>
                                                <input
                                                    type="number"
                                                    v-model="d.tkc_voucher_number"
                                                    class="form-control"
                                                    placeholder="伝票番号"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- フッター -->
                <div class="modal-footer border-0 bg-light">
                    <button type="button" class="btn btn-secondary" @click="close()">
                        <i class="fa-solid fa-xmark me-2"></i>
                        キャンセル
                    </button>
                    <button type="button" class="btn btn-primary px-4" @click="save()">
                        <i class="fa-solid fa-floppy-disk me-2"></i>
                        <span v-if="isOverwrite">上書き保存</span>
                        <span v-else>一括編集（登録しません）</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* モーダル全体 */
.modal-xl {
    max-width: 1440px;
}

.modal-content {
    border-radius: 12px;
}

/* ヘッダー */
.bg-gradient-primary {
    background: linear-gradient(135deg, #5acb58 0%, #4ba293 100%);
    color: white;
    padding: 1.25rem 1.5rem;
}

.modal-title {
    font-size: 1.25rem;
    letter-spacing: 0.01em;
}

/* 画像プレビュー */
.image-preview-card {
    background: #f8f9fa;
    border-radius: 12px;
    padding: 1.25rem;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.image-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: .5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.img-receipt {
    max-width: 100%;
    max-height: 60vh;
    object-fit: contain;
    border-radius: 4px;
    transition: transform 0.2s;
}

.img-receipt:hover {
    transform: scale(1.02);
}

.image-filename {
    font-size: 0.875rem;
    color: #6c757d;
    padding: 0.5rem;
    background: white;
    border-radius: 6px;
    text-align: center;
    word-break: break-all;
}

/* フォームセクション */
.form-section {
    background: #ffffff;
    border-radius: 10px;
    padding: 1.5rem;
    border: 1px solid #e9ecef;
}

.section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #e9ecef;
}

.form-label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
}

/* 明細カード */
.details-container {
    padding-right: 0.5rem;
}

.details-container::-webkit-scrollbar {
    width: 6px;
}

.details-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.details-container::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
}

.details-container::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
}

.detail-card {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 10px;
    padding: 1.25rem;
    margin-bottom: 1rem;
    transition: all 0.2s;
}

.detail-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #dee2e6;
}

.detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #dee2e6;
}

.detail-number {
    font-weight: 600;
    color: #667eea;
    font-size: 0.9rem;
}

/* フォーム要素 */
.form-control,
.form-select {
    border-radius: 6px;
    border: 1px solid #ced4da;
    font-size: 0.9rem;
    transition: all 0.2s;
}

.form-control:focus,
.form-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
}

.input-group-text {
    background-color: #f8f9fa;
    border: 1px solid #ced4da;
    border-radius: 6px 0 0 6px;
    font-weight: 500;
    color: #6c757d;
}

/* ボタン */
.btn {
    border-radius: 6px;
    font-weight: 500;
    transition: all 0.2s;
    font-size: 0.9rem;
}

.btn-primary {
    background: linear-gradient(135deg, #5acb58 0%, #4ba293 100%);
    border: none;
}

.btn-primary:hover {
    background: linear-gradient(135deg, #4ab847 0%, #3a8f82 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-outline-primary {
    border-color: #667eea;
    color: #667eea;
}

.btn-outline-primary:hover {
    background-color: #667eea;
    border-color: #667eea;
}

.btn-outline-danger {
    border-color: #dc3545;
    color: #dc3545;
}

.btn-outline-danger:hover {
    background-color: #dc3545;
    border-color: #dc3545;
}

/* フッター */
.modal-footer {
    padding: 1rem 1.5rem;
}

/* レスポンシブ調整 */
@media (max-width: 991px) {
    .image-wrapper {
        max-height: 40vh;
    }
}
</style>

<script>
import dropdownSelector from "components/dropdownSelector.vue";
import previewFile from 'components/previewFile.vue';
export default {
    components: {
        dropdownSelector,
        previewFile
     },
    props: {
        id: {
            type: String,
            default: 'bulk-editor'
        },
        receipt_image_ids: { type: Array, default: null }
    },
    data() {
        return {
            receipt_images: [],
            receipt: this.defaultReceipt(),
            account_titles: [],
            clients: [],
            partner_accounts: [],
            tags: [],
            tax_categories: [],
            errors: {},
        }
    },
    methods: {
        init() {
            axios.get('/api/master/dropdowns').then(res => {
                this.account_titles = res.data.account_titles;
                this.clients = res.data.clients;
                this.partner_accounts = res.data.partner_accounts;
                this.tags = res.data.tags;
                this.tax_categories = res.data.tax_categories;
            }).catch(err => {Alert.err(err)});
        },
        defaultReceipt() {
            /**
             * 基本的に空っぽのデータを用意する
             */
            return {
                purchase_date: null,
                client: null,
                tag: null,
                receipt_details: [
                    {
                        account_title: null,
                        amount: null,
                        tax_rate: 10,
                        tax: null,
                        tax_category: null,
                        description: null,
                        tkc_voucher_number: null
                    }
                ]
            };
        },
        addDetail() {
            this.receipt.receipt_details.push({
                receipt_id: this.receipt.id || null,
                account_title: null,
                amount: null,
                tax: 0,
                tax_rate: 10,
                tax_category: null,
                description: '',
                tkc_voucher_number: ''
            });
        },
        removeDetail(i) {
            this.receipt.receipt_details.splice(i, 1)
        },
        close() {
            this.$emit('close', this.id);
        },
        save() {
            if (_.isEqual(this.defaultReceipt(), this.receipt)) {
                Alert.warn("保存する内容がありません。");
                return;
            }
            axios.post("/api/tools/receipts", {
                ids: this.receipt_image_ids,
                receipt: this.receipt
            }).then(res => {
                Alert.msg("上書き保存しました。")
                this.$emit('saved');
                this.close();
            }).catch(err => {Alert.err(err)});
        },
        reload() {
            axios.get(`/api/tools/receipt-images/bulk`, {
                params: {
                    ids: this.receipt_image_ids
                }
            }).then(res => {
                this.receipt_images = res.data;
                setTimeout(() => {
                    if (!this.isOverwrite && this.representative) {
                        // 新規の場合は代表情報を反映する
                        this.receipt = this.representative.receipt;
                    } else {
                        this.receipt = this.defaultReceipt();
                    }
                });
            }).catch(err => {Alert.err(err)});
        },
        setTaxExclusive(detail) {
            const rate = Number(detail.tax_rate);
            if (!detail.amount || rate === 0) {
                detail.tax = 0;
                return;
            }
            detail.tax = Math.round(detail.amount * rate / 100);
        },
        setTaxInclusive(detail) {
            const rate = Number(detail.tax_rate);
            if (!detail.amount || rate === 0) {
                detail.tax = 0;
                return;
            }
            detail.tax = Math.round(detail.amount * rate / (100 + rate));
        },
        calcTax(detail) {
            if (!detail.amount || detail.tax_rate === 0) {
                detail.tax = 0;
                return;
            }
            if (!detail.tax) {
                this.setTaxInclusive(detail);
            }
        },
    },
    computed: {
        isOverwrite() {
            // 上書きモード判定（登録済み編集か
            if (this.receipt_images.findIndex((o) => o?.receipt?.registered_at) > -1) {
                return true;
            }
            return false;
        },
        representative() {
            // 領収書データを持っている最初のレコードを代表レコードとして返す
            const i = this.receipt_images.findIndex(o => {
                if (o?.receipt) {
                    return true;
                }
                return false;
            });
            if (i > -1) {
                return this.receipt_images[i];
            }
            return null;
        },
    },
    mounted() {
        this.init();
        $(`#${this.id}`).on("show.bs.modal", (e) => {
            this.reload();
        });
        $(`#${this.id}`).on("hidden.bs.modal", (e) => {
        });
    },
}
</script>